import {
  EXPORT_SIZE_PRESETS,
  ExportOrientation,
  ExportQuality,
  ExportScreenShape,
  ExportSizePreset,
  IExportLayout,
  MAX_PREVIEW_CANVAS_AREA,
  resolveExportLayout,
} from './mapExportPaper'
import { MAX_ZOOM_LEVEL } from './mapZoomRule'

export type ExportPhase =
  | 'idle'
  | 'preparing'
  | 'loadingTiles'
  | 'placingLabels'
  | 'rendering'
  | 'encoding'

export type MapExportFailureReason =
  | 'tilesTimeout'
  | 'bufferClamped'
  | 'zoomTooDeep'
  | 'exceedsDevice'
  | 'interrupted'
  | 'pageReloaded'
  | 'failed'

export class MapExportError extends Error {
  public constructor(
    public readonly reason: MapExportFailureReason,
    public readonly detail?: { width: number; height: number },
    // The GPU had no room for the map: its context was dropped or never made.
    public readonly isGpuShort = false,
  ) {
    super(reason)
    this.name = 'MapExportError'
  }
}

export type ExportRenderPlan =
  | { kind: 'single' }
  | { kind: 'blocked'; reason: 'exceedsDevice' | 'zoomTooDeep' }

export interface IExportPresetAvailability {
  pxWidth: number
  pxHeight: number
  isAvailable: boolean
}

// What a kind of device can take beyond its GPU's own limit. A phone runs three maps and keeps 2D
// canvases under iOS's area cap, so it frames tighter, previews coarser and never offers Actual size.
export interface IExportDeviceCaps {
  // Pane px kept around the page.
  frameInset: number
  // Largest preview canvas, in device px.
  previewArea: number
  // Largest file side and area, in px.
  maxSide: number
  maxArea: number
  actualSize: boolean
  // Density of the still that covers the preview while the export renders.
  stillDensity: number
  // Whether a render whose buffer came out short gets a second try.
  retriesClamped: boolean
}

export const DESKTOP_EXPORT_CAPS: IExportDeviceCaps = {
  frameInset: 48,
  previewArea: MAX_PREVIEW_CANVAS_AREA,
  maxSide: Infinity,
  maxArea: Infinity,
  actualSize: true,
  stillDensity: Infinity,
  retriesClamped: false,
}

// 16.7 MP is the 2D canvas area iOS allowed before 18; the PNG sheet must fit it. The still sits
// under the progress, so 1x serves, and a phone short of GPU memory on a first render usually
// has room on a second, once the first map is gone.
export const PHONE_EXPORT_CAPS: IExportDeviceCaps = {
  frameInset: 16,
  previewArea: 8_000_000,
  maxSide: 8192,
  maxArea: 16_777_216,
  actualSize: false,
  stillDensity: 1,
  retriesClamped: true,
}

// The largest file this device renders: the GPU's side (null while unknown) within the caps.
export interface IExportRenderLimits {
  maxSide: number | null
  maxArea: number
}

export const exportRenderLimits = (
  gpuMaxSide: number | null,
  caps: IExportDeviceCaps,
): IExportRenderLimits => ({
  maxSide:
    gpuMaxSide === null
      ? Number.isFinite(caps.maxSide)
        ? caps.maxSide
        : null
      : Math.min(gpuMaxSide, caps.maxSide),
  maxArea: caps.maxArea,
})

type PxSize = Pick<IExportLayout, 'pxWidth' | 'pxHeight'>

// The GPU renders the map alone; the 2D canvas holds the whole file, band and key strip too.
const fitsDevice = (
  map: PxSize,
  limits: IExportRenderLimits,
  file: PxSize = map,
): boolean =>
  (limits.maxSide === null ||
    Math.max(map.pxWidth, map.pxHeight) <= limits.maxSide) &&
  file.pxWidth * file.pxHeight <= limits.maxArea

export function planExportRender(
  layout: IExportLayout,
  exportZoom: number,
  limits: IExportRenderLimits,
  file: PxSize = layout,
): ExportRenderPlan {
  if (exportZoom > MAX_ZOOM_LEVEL) {
    return { kind: 'blocked', reason: 'zoomTooDeep' }
  }
  if (!fitsDevice(layout, limits, file)) {
    return { kind: 'blocked', reason: 'exceedsDevice' }
  }
  return { kind: 'single' }
}

export function presetAvailability(
  input: {
    orientation: ExportOrientation
    quality: ExportQuality
    screenShape: ExportScreenShape
  },
  limits: IExportRenderLimits,
): Record<ExportSizePreset, IExportPresetAvailability> {
  const result = {} as Record<ExportSizePreset, IExportPresetAvailability>
  EXPORT_SIZE_PRESETS.forEach(preset => {
    const { pxWidth, pxHeight } = resolveExportLayout({ ...input, preset })
    result[preset] = {
      pxWidth,
      pxHeight,
      isAvailable: fitsDevice({ pxWidth, pxHeight }, limits),
    }
  })
  return result
}

export function maxRenderSide(
  gl: Pick<
    WebGL2RenderingContext,
    | 'getParameter'
    | 'MAX_TEXTURE_SIZE'
    | 'MAX_RENDERBUFFER_SIZE'
    | 'MAX_VIEWPORT_DIMS'
  >,
): number {
  const viewportDims = gl.getParameter(gl.MAX_VIEWPORT_DIMS) as Int32Array
  return Math.min(
    gl.getParameter(gl.MAX_TEXTURE_SIZE) as number,
    gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) as number,
    viewportDims[0],
    viewportDims[1],
  )
}

export function drawingBufferMatches(
  gl: { drawingBufferWidth: number; drawingBufferHeight: number },
  canvas: { width: number; height: number },
  layout: IExportLayout,
): boolean {
  return (
    gl.drawingBufferWidth === canvas.width &&
    gl.drawingBufferHeight === canvas.height &&
    canvas.width === layout.pxWidth &&
    canvas.height === layout.pxHeight
  )
}
